import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const ROLE_OPTIONS = [
  { value: "caregiver", label: "Caregiver / Guardian" },
  { value: "participant", label: "Participant" },
  { value: "director", label: "Director" },
];

export default function Auth() {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("caregiver");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next");
  const safeNext = next && next.startsWith("/") && !next.startsWith("//") ? next : null;

  const dest = (r) => (r === "admin" ? "/admin" : r === "director" ? "/director" : "/participant");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const data = mode === "login" ? await login(email, password) : await register({ email, password, name, role });
      toast.success(`Welcome, ${data.name}!`);
      navigate(safeNext || dest(data.role));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-16 paper-texture" data-testid="auth-page">
      <div className="w-full max-w-md bg-white rounded-3xl border border-[#dfd8da] shadow-xl p-8">
        <h1 className="font-display text-3xl text-[#550001]">{mode === "login" ? "Welcome back" : "Create your account"}</h1>
        <p className="mt-2 text-sm text-[#545454]">
          {mode === "login" ? "Log in to your Console." : "One account can manage several participants."}
        </p>

        <div className="mt-5 grid grid-cols-2 rounded-lg overflow-hidden border border-[#dfd8da]">
          {["login", "register"].map((m) => (
            <button
              key={m}
              onClick={() => { setMode(m); setError(""); }}
              data-testid={`auth-tab-${m}`}
              className={`py-2.5 text-sm font-bold transition-colors duration-150 ${mode === m ? "bg-[#920e12] text-white" : "bg-white text-[#545454]"}`}
            >
              {m === "login" ? "Log in" : "Register"}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="mt-6 space-y-4">
          {mode === "register" && (
            <div>
              <Label htmlFor="name">Full name</Label>
              <input id="name" required value={name} onChange={(e) => setName(e.target.value)} data-testid="auth-name"
                className="mt-1 w-full h-11 px-3 rounded-lg border border-[#d6cecf]" />
            </div>
          )}
          <div>
            <Label htmlFor="email">Email</Label>
            <input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="auth-email"
              className="mt-1 w-full h-11 px-3 rounded-lg border border-[#d6cecf]" />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="auth-password"
              className="mt-1 w-full h-11 px-3 rounded-lg border border-[#d6cecf]" />
          </div>
          {mode === "register" && (
            <div>
              <Label htmlFor="role">I am a…</Label>
              <select id="role" value={role} onChange={(e) => setRole(e.target.value)} data-testid="auth-role"
                className="mt-1 w-full h-11 px-3 rounded-lg border border-[#d6cecf] bg-white">
                {ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
          )}
          {error && <p className="text-sm font-semibold text-[#991b1b]" data-testid="auth-error">{error}</p>}
          <Button type="submit" disabled={busy} className="w-full h-11 bg-[#920e12] hover:bg-[#550001] text-white font-bold" data-testid="auth-submit">
            {busy ? "Please wait…" : mode === "login" ? "Log in" : "Create account"}
          </Button>
        </form>

        <div className="mt-6 text-xs text-[#545454] bg-[#f9f6f6] rounded-lg p-3 border border-[#dfd8da]">
          <p className="font-bold mb-1">Demo logins</p>
          <p>Admin — tara.may@gmail.com / Improv2026!</p>
          <p>Director — director@improvaneer.com / Director2026!</p>
          <p>Caregiver — parent@improvaneer.com / Parent2026!</p>
        </div>
      </div>
    </div>
  );
}
